import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import {
  BRAND_PERSON_NAME,
  BRAND_HANDLE,
  BRAND_SITE_URL,
  BRAND_SUPPORT_EMAIL,
  BRAND_AGE,
  BRAND_CITY,
} from '@/lib/brandConfig';
import {
  cloudflareEdgeLanguageDetector,
  persistedUserLanguageDetector,
  querystringLanguageDetector,
  spanishDefaultGeoDetector,
  userExplicitLanguageDetector,
} from './cloudflareDetectors';
import en from './locales/en.json';
import es from './locales/es.json';

// Lazy load para idiomas menos comunes (reduce bundle inicial)
const loadPortuguese = () => import('./locales/pt.json');
const loadGerman = () => import('./locales/de.json');
const loadFrench = () => import('./locales/fr.json');
const loadItalian = () => import('./locales/it.json');
const loadDutch = () => import('./locales/nl.json');

const languageDetector = new LanguageDetector();
languageDetector.addDetector(querystringLanguageDetector);
languageDetector.addDetector(spanishDefaultGeoDetector);
languageDetector.addDetector(userExplicitLanguageDetector);
languageDetector.addDetector(cloudflareEdgeLanguageDetector);
languageDetector.addDetector(persistedUserLanguageDetector);

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: en
      },
      es: {
        translation: es
      }
    },
    fallbackLng: 'es',
    supportedLngs: ['en', 'es', 'pt', 'de', 'fr', 'it', 'nl'],
    load: 'languageOnly',
    detection: {
      // Worker cookies (user_language, user_country) + languagePolicy.ts documentan el orden completo.
      order: [
        'querystringLang',
        'spanishDefaultGeo',
        'userExplicitLanguage',
        'cloudflareEdge',
        'persistedUserLanguage',
        'navigator',
        'htmlTag',
        'path',
        'subdomain',
      ],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'user_language',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
      defaultVariables: {
        brandPersonName: BRAND_PERSON_NAME,
        brandHandle: BRAND_HANDLE,
        brandSiteUrl: BRAND_SITE_URL,
        brandSupportEmail: BRAND_SUPPORT_EMAIL,
        /** Solo el número (ej. "22"); en ES los strings ya añaden "años". En EN va entre comas ("…, 22, based…"). */
        brandAge: BRAND_AGE,
        brandCity: BRAND_CITY || "Argentina",
      },
    },
    react: {
      useSuspense: false, // Disable suspense to avoid loading delays
    },
  });

// Lazy loading de idiomas adicionales
i18n.on('languageChanged', async (lng) => {
  if (lng.startsWith('pt') && !i18n.hasResourceBundle('pt', 'translation')) {
    const pt = await loadPortuguese();
    i18n.addResourceBundle('pt', 'translation', pt.default);
  } else if (lng.startsWith('de') && !i18n.hasResourceBundle('de', 'translation')) {
    const de = await loadGerman();
    i18n.addResourceBundle('de', 'translation', de.default);
  } else if (lng.startsWith('fr') && !i18n.hasResourceBundle('fr', 'translation')) {
    const fr = await loadFrench();
    i18n.addResourceBundle('fr', 'translation', fr.default);
  } else if (lng.startsWith('it') && !i18n.hasResourceBundle('it', 'translation')) {
    const it = await loadItalian();
    i18n.addResourceBundle('it', 'translation', it.default);
  } else if (lng.startsWith('nl') && !i18n.hasResourceBundle('nl', 'translation')) {
    const nl = await loadDutch();
    i18n.addResourceBundle('nl', 'translation', nl.default);
  }
});

export default i18n;
